import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { InstituteOverview } from '@/types/instituteDashboard';
import type { Institute, InstituteBranding } from '@/types/institute';
import type { InstituteRole } from '@/types/role';
import type { InstituteUpdatePayload } from '@/features/platform/institutes/schemas/institute.schema';
import type { RolePayload } from '../roles/schemas/role.schema';

/**
 * Every call an institute makes about itself. There is no id in any of these paths
 * — the server takes the institute from the caller's token, so there is nothing
 * for a client to point at the wrong institute.
 */
export const instituteApi = {
  getProfile: () => unwrap(apiClient.get<ApiSuccess<Institute>>('/institute/profile')),

  updateProfile: (payload: InstituteUpdatePayload) =>
    unwrap(apiClient.patch<ApiSuccess<Institute>>('/institute/profile', payload)),

  /**
   * Every figure on the console's first screen, in one request.
   *
   * Blocks the caller may not read come back null rather than zeroed, so the
   * screen prints an em dash instead of inventing a number.
   */
  getOverview: () =>
    unwrap(apiClient.get<ApiSuccess<InstituteOverview>>('/institute/dashboard/institute')),

  /** Name and whether a logo exists — what the sidebar needs on every screen. */
  getBranding: () => unwrap(apiClient.get<ApiSuccess<InstituteBranding>>('/institute/branding')),

  /** Private, like every upload — needs the access token, so use SecureImage. */
  logoPath: () => '/institute/profile/logo',

  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    // Content-Type is left unset on purpose: the browser must add the
    // multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<Institute>>('/institute/profile/logo', form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  removeLogo: () => unwrap(apiClient.delete<ApiSuccess<null>>('/institute/profile/logo')),

  listRoles: () => unwrap(apiClient.get<ApiSuccess<InstituteRole[]>>('/institute/roles')),

  createRole: (payload: RolePayload) =>
    unwrap(apiClient.post<ApiSuccess<InstituteRole>>('/institute/roles', payload)),

  updateRole: (uuid: string, payload: RolePayload) =>
    unwrap(apiClient.patch<ApiSuccess<InstituteRole>>(`/institute/roles/${uuid}`, payload)),

  deleteRole: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/roles/${uuid}`)),
};
